import { mediaQueries, palette } from '@centivo/react-components'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useGlobalState } from 'src/context/global'
import styled, { css } from 'styled-components'

export interface FooterProps {
  isBannerOpen?: boolean
}

const EligibilityFooter = styled.footer<FooterProps>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 90%;
  max-width: 1106px;
  margin: 84px auto 0;
  border-top: 1px solid ${({ theme }) => theme.palette.brand.lightPurple};
  padding-top: 20px;

  > div.content {
    color: #212121;
    font-size: 1rem;
    letter-spacing: -0.32px;
    font-weight: 400;
    margin-bottom: 1.25rem;
  }

  > nav ul {
    display: flex;

    a,
    p {
      font-size: 1rem;
      color: #212121;
      letter-spacing: -0.32px;
      font-weight: 400;
    }

    a:hover,
    a:focus {
      text-decoration: underline;
    }

    li:not(:last-child) {
      padding-right: 8px;
      margin-right: 8px;
      border-right: 1px solid #cac1e6;
    }
  }

  @media ${mediaQueries.mobile} {
    margin-top: 40px;
    padding-bottom: 54px;
    flex-direction: column;

    > nav ul {
      flex-wrap: wrap;
    }
  }
`

const FooterContainer = styled.footer<FooterProps>`
  display: flex;
  flex-direction: row;
  font-size: 11px;
  font-weight: 500;
  min-height: 5rem;
  padding-bottom: 1.25rem;
  padding-top: 1rem;
  width: 100vw;
  align-items: center;
  transition: margin-bottom 1s ease;

  ${({ theme, isBannerOpen }) => {
    switch (theme.template) {
      case 'template-4':
        return css`
          ${isBannerOpen ? 'margin-bottom: 2.75rem;' : ''}

          background-color: ${palette.brand.beige};
          color: ${palette.brand.darkGreen};

          > nav ul {
            color: ${palette.brand.darkGreen};

            a {
              font-weight: 400;
            }
          }
        `

      default:
        return css`
          ${isBannerOpen ? 'margin-bottom: 1.5rem;' : ''}

          background-color: ${palette.brand.teal};
          color: ${palette.neutral.white};

          > nav ul {
            color: ${palette.neutral.white};

            a {
              font-weight: 600;
            }
          }
        `
    }
  }}

  > nav {
    margin-left: auto;
    margin-right: 6rem;
  }

  > nav ul {
    display: flex;

    a {
      color: inherit;
      font-size: 1rem;

      &:hover,
      &:focus {
        text-decoration: underline;
      }
    }

    li:not(:first-child) {
      margin-left: 2rem;
    }
  }

  > .content {
    align-self: center;
    flex-grow: 1;
    padding: 0 1.25rem;

    ${({ theme }) => theme.screenWidth}

    @media ${mediaQueries.desktop} {
      padding: 0 2rem;
      justify-content: right;
    }

    a {
      color: inherit;
      &:hover {
        color: inherit;
      }
    }
  }
`

export const Footer: React.FC<FooterProps> = ({ isBannerOpen, children }) => {
  const [{ appConfig }] = useGlobalState()

  const Container = useMemo(
    () =>
      appConfig.template === 'template-eligibility'
        ? EligibilityFooter
        : FooterContainer,
    [appConfig.template]
  )

  return (
    <Container isBannerOpen={isBannerOpen}>
      {children}
      <nav>
        <ul>
          {appConfig.template === 'template-eligibility' && (
            <li>
              <p>All rights reserved</p>
            </li>
          )}
          <li>
            <a
              href="https://centivo.com/terms/"
              target="_blank"
              rel="noreferrer"
            >
              Terms of Use
            </a>
          </li>
          <li>
            <Link to="/privacy-policy">Privacy Policy</Link>
          </li>
        </ul>
      </nav>
    </Container>
  )
}
